import { createCustomerFromOnboarding, fetchCustomerById } from "../../services/customerService";
import { fetchBrands, saveBrandDetails } from "../../services/brandService";
import { needsBrandDetails } from "./brandDetailsFromProfile";
import type { QuickStartValues } from "./QuickStartForm";
import { createAgent, fetchAgentsByCustomer } from "../../services/agentService";
import { importMediaFromUrl } from "../../services/contentService";
import { getFreshIdToken } from "../../auth/getFreshIdToken";
import { defaultModelForAgentType } from "../../components/AIAgents/ChatWithAgent/config/modelOptions";
import { AGENT_TYPE } from "../../constants/agentTypes";
import { toolsForAgentType } from "../../constants/agentChips";
import { debug } from "../../utils/debugLogger";

/**
 * Onboarding in two steps (SGL-512, on top of SGL-448's form-first rule).
 *
 * Step 1, the Brand: created from a few typed fields; the one thing that must
 * succeed. It stays ONBOARDING. Whatever the website read found (logo, colours,
 * voice) is saved right away as a Brand-details *draft*, so leaving here and
 * coming back never reads the website or copies the logo twice.
 *
 * Step 2, Brand details: the owner checks the draft and confirms. That save is
 * what makes the Brand ACTIVE (customer-management, SGL-513), and only then are
 * the agents created.
 *
 * The draft stays ONBOARDING because it has no service area: customer-
 * management activates a Brand only when its details have a name AND a
 * service area (common/brand_status.py `details_complete`). If that rule
 * changes, the draft save below must change with it.
 */

export interface QuickStartInput {
  businessName: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  website: string;
  services: string[];
  contactEmail: string;
  userId: string;
}

export type ColorSlot = "primary" | "secondary" | "accent" | "other";

/** Step 2's fields, pre-filled from the website read; every one editable. */
export interface BrandDetailsDraft {
  /** Our own CDN copy of the logo. Never a third-party address. */
  logoUrl: string;
  logoMediaId: string;
  /** The logo as found on the owner's website; only ever copied, never saved. */
  sourceLogoUrl: string;
  /** The mark for dark backgrounds (SGL-547): ours, copied or generated. */
  logoUrlDark: string;
  logoMediaIdDark: string;
  /** The website's own dark-background logo, when it has one; only ever copied. */
  sourceLogoUrlDark: string;
  colors: { value: string; type: ColorSlot }[];
  /** True when the colours were measured off the site, not given by the owner. */
  colorsDetected: boolean;
  tagline: string;
  brandVoice: string;
  targetAudience: string;
}

export const EMPTY_DETAILS: BrandDetailsDraft = {
  logoUrl: "",
  logoMediaId: "",
  sourceLogoUrl: "",
  logoUrlDark: "",
  logoMediaIdDark: "",
  sourceLogoUrlDark: "",
  colors: [],
  colorsDetected: false,
  tagline: "",
  brandVoice: "",
  targetAudience: "",
};

export interface QuickStartResult {
  customerId: string;
  /** Agent the owner lands in (the Social Media Manager), if it was created. */
  landingAgentId: string | null;
  /** Agent types that failed to create; the owner can add them later. */
  failedAgents: string[];
  /** The team that was created, for the cards shown when setup finishes. */
  agents: CreatedAgent[];
  /** The new Brand's name, so the app can switch to it when setup finishes. */
  businessName: string;
  /** Whether the server-side starter knowledge was started (set by the page). */
  knowledgeStarted?: boolean;
}

/** What step 1 hands to step 2. */
export interface CreatedBrand {
  customerId: string;
  /** Set once a Brand-details record exists (the draft), so step 2 updates it. */
  brandId: string | null;
  details: BrandDetailsDraft;
  /** Set when the logo was found but couldn't be copied; the owner can upload one. */
  logoError?: string;
}

export interface CreatedAgent {
  id: string;
  firstName: string;
  role: string;
  avatarUrl: string;
  type: string;
}

const answer = (value: unknown) => ({ answer_value: value });

function serviceAreaFor(input: Pick<QuickStartInput, "city" | "state">): string[] {
  const place = [input.city.trim(), input.state.trim()].filter(Boolean).join(", ");
  return place ? [place] : [];
}

/** Instructions written only from what the owner typed: no invented facts. */
function instructionsFor(agentType: string, input: QuickStartInput): string {
  const where = [input.city.trim(), input.state.trim()].filter(Boolean).join(", ");
  const services = input.services.length
    ? ` The business offers: ${input.services.join(", ")}.`
    : "";
  const base =
    `You work for ${input.businessName.trim()}${where ? `, based in ${where}` : ""}.${services} ` +
    "Use the Brand details for everything else about the business. Never invent prices, " +
    "customer names, reviews or results; if something is missing, ask the owner.";
  if (agentType === AGENT_TYPE.CONTENT_CREATOR) {
    return `You are Maya, the Social Media Manager. ${base} Create posts, images and blogs in the brand's voice and send everything to the owner for approval before it is published.`;
  }
  if (agentType === AGENT_TYPE.VISIBILITY_MANAGER) {
    return `You are Vera, the AI Visibility Manager. ${base} Check whether ChatGPT, Gemini and Perplexity recommend the business, explain the gaps in plain words, and suggest what would close them.`;
  }
  return `You are Sam, the General Assistant. ${base}`;
}

/** The starting team. A first name with the role kept visible, and a picture
 *  from the designed set in public/avatars/agents (Sourav, 2026-10-17). The
 *  owner can rename them or change the picture any time. */
const AGENTS_FOR_EVERY_BRAND = [
  { type: AGENT_TYPE.CONTENT_CREATOR, firstName: "Maya", role: "Social Media Manager", avatar: "maya" },
  { type: AGENT_TYPE.GENERAL, firstName: "Sam", role: "General Assistant", avatar: "sam" },
  // Created on every Plan (SGL-449): Starter owners see it labelled Pro and
  // get a preview with an upgrade instead of a chat; chat-service refuses
  // their messages to it. Upgrading unlocks this same agent.
  { type: AGENT_TYPE.VISIBILITY_MANAGER, firstName: "Vera", role: "AI Visibility Manager", avatar: "vera" },
  // The AI Receptionist is deliberately not created here: it needs a
  // GoHighLevel account and would only fail until one is connected.
];

/** Full address, so the picture loads wherever the agent is shown. */
const avatarUrl = (name: string) =>
  `${typeof window !== "undefined" ? window.location.origin : ""}/avatars/agents/${name}.svg`;

/** The Brand-details payload, in the shape submitBrandDetailsFromOnboarding reads. */
function detailsAnswers(input: QuickStartInput, details: BrandDetailsDraft, serviceAreas: string[]) {
  return {
    brand_name: answer(input.businessName.trim()),
    business_type: answer("SERVICE"),
    website: answer(input.website.trim()),
    service_area: answer(serviceAreas),
    services_offered: answer(input.services),
    brand_tagline: answer(details.tagline.trim()),
    brand_voice: answer(details.brandVoice.trim()),
    target_audience: answer(details.targetAudience.trim()),
    // Only our own copy; the website's address is refused by customer-management.
    logo_url: details.logoUrl,
    logo_media_id: details.logoMediaId,
    logo_url_dark: details.logoUrlDark,
    logo_media_id_dark: details.logoMediaIdDark,
    // The owner's colours in slot order; an empty list, never a default palette.
    brand_colors: orderedHexes(details.colors),
  };
}

const SLOT_ORDER: ColorSlot[] = ["primary", "secondary", "accent", "other"];
function orderedHexes(colors: BrandDetailsDraft["colors"]): string[] {
  return [...colors]
    .sort((a, b) => SLOT_ORDER.indexOf(a.type) - SLOT_ORDER.indexOf(b.type))
    .map((c) => c.value)
    .filter(Boolean);
}

type CopiedLogos = Partial<Pick<BrandDetailsDraft, "logoUrl" | "logoMediaId" | "logoUrlDark" | "logoMediaIdDark">>;

/**
 * Copy the website's logo into our storage. Returns null when there is none;
 * throws with a plain reason when the copy fails (the caller shows it and
 * offers an upload). The dark-background logo (SGL-547) is best effort: copied
 * from the website's own dark source when it has one, else generated by the
 * same import call. Its failure is logged and never reaches the caller, and an
 * owner's existing dark logo is never replaced.
 */
async function copyWebsiteLogo(
  customerId: string,
  details: BrandDetailsDraft,
  token: string
): Promise<CopiedLogos | null> {
  if (details.logoUrl || !details.sourceLogoUrl) return null;
  const wantDark = !details.logoUrlDark;
  const darkSource = wantDark ? details.sourceLogoUrlDark : "";
  const media = await importMediaFromUrl(
    customerId,
    details.sourceLogoUrl,
    token,
    "logo",
    wantDark && !darkSource ? { darkVariant: "generate" } : undefined
  );
  const out: CopiedLogos = {
    logoUrl: media.cloudfront_url,
    logoMediaId: media.media_id,
  };
  if (wantDark && !darkSource && media.dark?.cloudfront_url) {
    out.logoUrlDark = media.dark.cloudfront_url;
    out.logoMediaIdDark = media.dark.media_id;
  }
  if (darkSource) {
    try {
      const dark = await importMediaFromUrl(customerId, darkSource, token, "logo");
      out.logoUrlDark = dark.cloudfront_url;
      out.logoMediaIdDark = dark.media_id;
    } catch (e) {
      console.error("[SGL-547] Copying the website's dark-background logo failed:", e);
    }
  }
  return out;
}

/**
 * Step 1: create the Brand (ONBOARDING) and save what the website read found
 * as a draft. Throws only if the Brand itself could not be created; a failed
 * logo copy or draft save is logged and reported, never blocking.
 */
export async function createBrand(
  input: QuickStartInput,
  found: BrandDetailsDraft,
  idToken: string,
  { saveDraft = true }: { saveDraft?: boolean } = {}
): Promise<CreatedBrand> {
  const customerId = await createCustomerFromOnboarding(
    {
      business_name: input.businessName.trim(),
      city: input.city.trim(),
      state: input.state.trim(),
      zip_code: input.zip.trim() || undefined,
      country: input.country.trim(),
      contact_person_email: input.contactEmail,
      created_by: input.userId,
    },
    idToken
  );
  // createCustomerFromOnboarding refreshed the session so the new token lists
  // this Brand; every later call must use that token, not the one passed in.
  const token = await getFreshIdToken();

  let details = { ...found };
  let logoError: string | undefined;
  try {
    const copied = await copyWebsiteLogo(customerId, details, token);
    if (copied) details = { ...details, ...copied };
  } catch (e) {
    console.error("[SGL-512] Copying the website logo failed:", e);
    logoError = "I couldn't copy the logo from your website. You can upload it here instead.";
  }

  // The draft: everything found, but no service area, so the Brand stays
  // ONBOARDING until step 2. Losing it only means step 2 starts emptier.
  let brandId: string | null = null;
  if (saveDraft) {
    try {
      const saved = await saveBrandDetails(customerId, detailsAnswers(input, details, []), token, null);
      brandId = saved.brandId ?? null;
      if (saved.statusUpdate === "activated") {
        console.error("[SGL-512] The step-1 draft activated the Brand; check details_complete in customer-management", customerId);
      }
    } catch (e) {
      console.error("[SGL-512] Saving the Brand-details draft failed:", e);
    }
  }
  return { customerId, brandId, details, logoError };
}

/** The Brand's agents, if it already has any (a resumed step 2). */
async function existingAgents(customerId: string, token: string): Promise<CreatedAgent[]> {
  const data = await fetchAgentsByCustomer(customerId, token);
  const list: any[] = Array.isArray(data) ? data : data?.agents ?? data?.data ?? [];
  return list
    .filter((a) => a?.ai_agent_id)
    .map((a) => ({
      id: a.ai_agent_id,
      firstName: String(a.agent_name || "").split("·")[0].trim(),
      role: String(a.agent_name || "").split("·")[1]?.trim() || "",
      avatarUrl: a.profile_picture_url || "",
      type: a.agent_type || "",
    }));
}

/**
 * Step 2: save the Brand details the owner confirmed (this makes the Brand
 * ACTIVE), then create the agents. Throws if the details could not be saved,
 * so no agent is ever created for a Brand that is still ONBOARDING.
 */
export async function saveDetailsAndCreateTeam(
  brand: Pick<CreatedBrand, "customerId" | "brandId">,
  input: QuickStartInput,
  details: BrandDetailsDraft
): Promise<QuickStartResult> {
  const { customerId } = brand;
  const token = await getFreshIdToken();

  const saved = await saveBrandDetails(
    customerId,
    detailsAnswers(input, details, serviceAreaFor(input)),
    token,
    brand.brandId
  );
  if (saved.statusUpdate === "failed") {
    // The details are saved; only the status flip failed (logged server-side
    // as BRAND_STATUS_FLIP_FAILED). Loud here too, but not a reason to stop.
    console.error("[SGL-512] Brand details saved but the Brand was not activated", customerId);
  }

  // A resumed step 2 may follow a Brand that already got its team (an older
  // flow, or a retry): never create a second Maya.
  let already: CreatedAgent[] = [];
  try {
    already = await existingAgents(customerId, token);
  } catch (e) {
    console.error("[SGL-512] Couldn't check for existing agents; creating the team:", e);
  }
  if (already.length > 0) {
    const landing = already.find((a) => a.type === AGENT_TYPE.CONTENT_CREATOR) ?? already[0];
    return { customerId, landingAgentId: landing.id, failedAgents: [], agents: already, businessName: input.businessName.trim() };
  }

  return createTeam(customerId, input, token);
}

/**
 * The form path ("Use the form instead"): the owner typed everything there is,
 * so both steps run back to back. Throws only if the Brand itself could not be
 * created; a failed details save is logged and the agents are still created,
 * as before, so the owner is never locked out (SGL-448).
 */
export async function quickStartOnboarding(
  input: QuickStartInput,
  idToken: string
): Promise<QuickStartResult> {
  // No draft: the details are saved in full a moment later.
  const brand = await createBrand(input, EMPTY_DETAILS, idToken, { saveDraft: false });
  try {
    return await saveDetailsAndCreateTeam(brand, input, brand.details);
  } catch (e) {
    console.error("[SGL-512] Saving Brand details from the form failed; creating the team anyway:", e);
    return createTeam(brand.customerId, input, await getFreshIdToken());
  }
}

/** Create the plan's agents. Each is independent: one failing never blocks the others. */
async function createTeam(customerId: string, input: QuickStartInput, token: string): Promise<QuickStartResult> {
  let landingAgentId: string | null = null;
  const failedAgents: string[] = [];
  const agents: CreatedAgent[] = [];
  for (const agent of AGENTS_FOR_EVERY_BRAND) {
    const name = `${agent.firstName} · ${agent.role}`;
    const picture = avatarUrl(agent.avatar);
    try {
      const model = defaultModelForAgentType(agent.type);
      const result = await createAgent(
        {
          agent_name: name,
          profile_picture_url: picture,
          instructions: instructionsFor(agent.type, input),
          model: model.model,
          provider: model.provider,
          customer_id: customerId,
          agent_type: agent.type,
          // Same per-type tool list the Create Agent form uses; the stored list is what chat attaches.
          tools: toolsForAgentType(agent.type),
        },
        token
      );
      const id = result?.data?.ai_agent_id as string | undefined;
      if (agent.type === AGENT_TYPE.CONTENT_CREATOR && id) landingAgentId = id;
      if (id) agents.push({ id, firstName: agent.firstName, role: agent.role, avatarUrl: picture, type: agent.type });
    } catch (e) {
      debug.error(`quickStartOnboarding: creating ${name} failed`, e);
      failedAgents.push(agent.role);
    }
  }
  return { customerId, landingAgentId, failedAgents, agents, businessName: input.businessName.trim() };
}

/**
 * Step 2 for a Brand the owner left after step 1, read back from what step 1
 * saved: no second website read, no second logo copy. Null when the Brand
 * doesn't need it.
 */
export async function loadResume(
  customerId: string,
  token: string
): Promise<{ brand: CreatedBrand; values: QuickStartValues } | null> {
  const [customer, { brands }] = await Promise.all([
    fetchCustomerById(customerId, token),
    fetchBrands(customerId, token),
  ]);
  const saved = brands[0];
  if (!needsBrandDetails(customer?.status, saved)) return null;
  const values: QuickStartValues = {
    businessName: customer.business_name || saved?.brand?.name || "",
    city: customer.city || "",
    state: customer.state || "",
    zip: customer.zip_code || "",
    country: customer.country || "",
    website: saved?.brand?.website_url || "",
    services: saved?.brand?.services ?? [],
  };
  const details: BrandDetailsDraft = {
    ...EMPTY_DETAILS,
    logoUrl: saved?.visual?.logo_url || "",
    logoMediaId: saved?.visual?.logo_media_id || "",
    logoUrlDark: saved?.visual?.logo_url_dark || "",
    logoMediaIdDark: saved?.visual?.logo_media_id_dark || "",
    colors: (saved?.visual?.colors ?? []).filter((c) => c?.hex).map((c) => ({ value: c.hex, type: c.role })),
    tagline: saved?.brand?.tagline || "",
    brandVoice: saved?.content_guidelines?.brand_voice || "",
    targetAudience: saved?.content_guidelines?.target_audience || "",
  };
  return { brand: { customerId, brandId: saved?.brand_id ?? null, details }, values };
}
